import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarClock, Copy, Loader2, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ForecastStrip } from "@/pages/CoolingTracker";

// Member read-only cooling view: only counts + date-wise + refill-unblock Rel IDs.
export default function MyCooling() {
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState(null);
  const [agencyId, setAgencyId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(25);

  useEffect(() => {
    api.get("/cooling/my-agencies")
      .then(({ data }) => {
        const list = data.agencies || [];
        setAgencies(list);
        const want = new URLSearchParams(window.location.search).get("agency");
        const pick = list.find((a) => a.id === want) || list[0];
        if (pick) setAgencyId(pick.id);
      })
      .catch((e) => { toast.error(formatApiError(e)); setAgencies([]); });
  }, []);

  useEffect(() => {
    if (!agencyId) return;
    setLoading(true);
    api.get(`/cooling/my-summary?agency_id=${encodeURIComponent(agencyId)}`)
      .then(({ data }) => setData(data))
      .catch((e) => { toast.error(formatApiError(e)); setData(null); })
      .finally(() => setLoading(false));
  }, [agencyId]);

  const forecast = useMemo(() => {
    if (!data) return null;
    const series = data.series.slice(0, days);
    const peak = series.reduce((p, s) => (s.count > (p?.count || 0) ? s : p), null);
    return {
      today: data.today,
      series,
      totals: { ...data.totals, window: series.reduce((a, s) => a + s.count, 0), peak },
    };
  }, [data, days]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${label}`);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F8F3]" data-testid="my-cooling-page">
      <div className="bg-white border-b border-[#EDEDE5] px-3 sm:px-6 py-3 flex items-center gap-2 sticky top-0 z-10">
        <button onClick={() => navigate("/chat")} className="p-1.5 rounded hover:bg-[#F0F0EA]" data-testid="my-cooling-back">
          <ArrowLeft className="w-5 h-5 text-[#385C4B]" />
        </button>
        <CalendarClock className="w-5 h-5 text-[#385C4B] flex-shrink-0" />
        <h1 className="text-base sm:text-lg font-bold text-[#1C1C19]">Cooling</h1>
        {data && <span className="ml-auto text-[11px] font-mono text-[#6B6B61]">Today: {data.today}</span>}
      </div>

      <div className="max-w-4xl mx-auto px-3 sm:px-6 py-4 space-y-4">
        {agencies === null ? (
          <Centered><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</Centered>
        ) : agencies.length === 0 ? (
          <>
            <Kpis data={{ ready_today: 0, totals: { next_7: 0, next_14: 0 }, refill_unblock: { count: 0 } }} />
            <Centered testid="my-cooling-no-agency">
              <ClipboardList className="w-6 h-6 inline mb-1 opacity-40" /><br />
              Aapki agency ka cooling data abhi upload nahi hua. Admin upload karega to yahan dikhne lagega.
            </Centered>
          </>
        ) : (
          <>
            <AgencyPicker agencies={agencies} agencyId={agencyId} setAgencyId={setAgencyId} />
            {loading || !data ? (
              <Centered><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</Centered>
            ) : (
              <>
                <Kpis data={data} />
                <ForecastStrip
                  forecast={forecast}
                  forecastDays={days}
                  setForecastDays={setDays}
                  onPeakClick={() => {}}
                  emptyHint="Admin ne abhi naya cooling data upload nahi kiya."
                />
                <DateList series={forecast.series} />
                <RefillUnblock info={data.refill_unblock} copy={copy} />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Centered({ children, testid }) {
  return <div className="py-10 text-center text-sm text-[#6B6B61]" data-testid={testid}>{children}</div>;
}

function AgencyPicker({ agencies, agencyId, setAgencyId }) {
  if (agencies.length === 1) {
    return (
      <div className="text-xs font-mono text-[#385C4B] truncate" data-testid="my-cooling-agency-name">
        📍 <b>{agencies[0].name}</b>
      </div>
    );
  }
  return (
    <select
      value={agencyId}
      onChange={(e) => setAgencyId(e.target.value)}
      className="w-full sm:w-auto px-2 py-2 text-sm border border-[#DCDCD3] rounded bg-white"
      data-testid="my-cooling-agency-select"
    >
      {agencies.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
    </select>
  );
}

function Kpis({ data }) {
  const items = [
    { k: "ready", label: "Aaj Ready", v: data.ready_today, c: "text-emerald-700" },
    { k: "next7", label: "Next 7 din", v: data.totals.next_7, c: "text-blue-700" },
    { k: "next14", label: "Next 14 din", v: data.totals.next_14, c: "text-amber-700" },
    { k: "unblock", label: "Refill Unblock", v: data.refill_unblock.count, c: "text-[#C8553D]" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3" data-testid="my-cooling-kpis">
      {items.map((i) => (
        <div key={i.k} className="bg-white border border-[#EDEDE5] rounded-lg p-3">
          <div className={`text-[10px] font-mono uppercase tracking-wider ${i.c}`}>{i.label}</div>
          <div className={`text-2xl font-bold mt-1 ${i.c}`} data-testid={`my-cooling-kpi-${i.k}`}>{i.v || 0}</div>
        </div>
      ))}
    </div>
  );
}

function DateList({ series }) {
  const rows = series.filter((s) => s.count > 0);
  return (
    <div className="bg-white border border-[#EDEDE5] rounded-lg" data-testid="my-cooling-date-list">
      <div className="px-3 py-2 border-b border-[#EDEDE5] text-[10px] font-mono uppercase tracking-[0.2em] text-[#385C4B]">
        Date-wise ready hone wale
      </div>
      {rows.length === 0 ? (
        <Centered>Koi consumer is window mein ready nahi ho raha.</Centered>
      ) : (
        <div className="divide-y divide-[#F0F0EA]">
          {rows.map((s) => (
            <div key={s.date} className="px-3 py-2 flex items-center text-sm" data-testid={`my-cooling-date-${s.date}`}>
              <span className="font-mono text-[#1C1C19]">{s.day_short} {s.day_num} {s.month_short}</span>
              <span className="ml-auto font-bold text-[#385C4B]">{s.count} consumer{s.count > 1 ? "s" : ""}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RefillUnblock({ info, copy }) {
  const ids = info.relationship_ids || [];
  return (
    <div className="bg-white border border-[#C8553D]/40 rounded-lg" data-testid="my-cooling-refill-unblock">
      <div className="px-3 py-2 border-b border-[#EDEDE5] flex items-center gap-2">
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#C8553D]">⚠ Refill Unblock ({ids.length})</span>
        {ids.length > 1 && (
          <button
            onClick={() => copy(ids.join("\n"), `${ids.length} IDs`)}
            className="ml-auto text-[10px] font-mono uppercase px-2 py-1 rounded border border-[#DCDCD3] hover:border-[#C8553D] flex items-center gap-1"
            data-testid="my-cooling-copy-all-unblock"
          >
            <Copy className="w-3 h-3" /> Copy all
          </button>
        )}
      </div>
      {ids.length === 0 ? (
        <Centered>Koi refill unblock pending nahi hai.</Centered>
      ) : (
        <div className="divide-y divide-[#F0F0EA]">
          {ids.map((id) => (
            <div key={id} className="px-3 py-2 flex items-center gap-2" data-testid={`my-cooling-unblock-${id}`}>
              <span className="font-mono text-sm text-[#1C1C19] break-all">{id}</span>
              <button
                onClick={() => copy(id, "ID")}
                className="ml-auto p-1.5 rounded hover:bg-[#F0F0EA] flex-shrink-0"
                data-testid={`my-cooling-copy-${id}`}
                title="Copy"
              >
                <Copy className="w-3.5 h-3.5 text-[#6B6B61]" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
